"use client";
import ApiLoader from "@/components/common/ApiLoader";
import Footer from "@/components/common/Footer";
import Banner from "@/components/country/Banner";
import TopUniversity from "@/components/country/TopUniversity";
import TryAiCourseFindBanner from "@/components/dashboard/TryAiCourseFindBanner";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { CountryInfo } from "@/store/types/countryTypes";
import axiosInstance from "@/utils/axiosInstance";
import { formatCountryUrl } from "@/utils/formatCountryUrl";
import { useParams } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { client } from "@/sanity/client";
import { NewNavBar } from "@/components/common/NewNavBar";
import { CountryStickyTabs, CountryTabItem } from "./CountryStickyTabs";
import CountryHighlights from "./CountryHighlights";
import CountryWhyStudy from "./CountryWhyStudy";
import CountryCostOfStudying from "./CountryCostOfStudying";
import CountryEntryRequirements from "./CountryEntryRequirements";
import CountryCostOfLiving from "./CountryCostOfLiving";
import CountryPostStudyWorkVisa from "./CountryPostStudyWorkVisa";
import CountryJobsAfterGraduation from "./CountryJobsAfterGraduation";
import CountryStudentVisaProcess from "./CountryStudentVisaProcess";
import CountryFaqs from "./CountryFaqs";
import CountryBottomCta from "./CountryBottomCta";
import React from "react";

export const getCountryByIdQuery = `
  *[_type == "countryData" && (id == $id || lower(countryName) == lower($countryName))][0]{
    id,
    countryName,
    currency,
    description,

    "bgImg": bgImg.asset->url,

    slides[]{
      title,
      description,
      "image": image.asset->url
    },

    "galleryImages": galleryImages[].asset->url,
    highlights[]{
      title,
      subtitle,
      iconType,
      "iconImage": iconImage.asset->url
    },
    whyStudyReasons{
      heading,
      subheading,
      reasons[]{
        number,
        title,
        description
      }
    },
    costOfStudying{
      heading,
      subheading,
      tuitionTable[]{
        degreeType,
        duration,
        averageTuition
      },
      disclaimerText,
      exclusionsNote{
        title,
        description
      }
    },
    entryRequirements{
      heading,
      subheading,
      requirementsTable[]{
        levelOfEducation,
        minQualification,
        englishRequirement
      }
    },
    costOfLiving{
      heading,
      subheading,
      livingTable[]{
        expense,
        avgCost
      },
      ruleOfThumb{
        title,
        description
      },
      cityCostsHeading,
      cityCosts[]{
        cityName,
        monthlyCost,
        description
      }
    },
    postStudyWorkVisa{
      heading,
      subheading,
      topCards[]{
        iconType,
        "iconImage": iconImage.asset->url,
        title,
        description
      },
      timelineSteps[]{
        stepNumber,
        title,
        description
      }
    },
    jobsAfterGraduation{
      heading,
      subheading,
      jobCards[]{
        title,
        category,
        salaryRange
      },
      partTimeCallout{
        title,
        description
      }
    },
    studentVisaProcess{
      heading,
      subheading,
      totalTime,
      totalCost,
      successRate,
      steps[]{
        stepNumber,
        title,
        description,
        timeBadge,
        tags
      }
    },
    faqsSection{
      heading,
      subheading,
      items[]{
        question,
        answer
      }
    }
  }
`;

const ALL_COUNTRY_TABS: CountryTabItem[] = [
  { id: "overview", label: "Overview" },
  { id: "why-study", label: "Why Study" },
  { id: "universities", label: "Top Universities" },
  { id: "cost-of-studying", label: "Cost of Studying" },
  { id: "entry-requirements", label: "Entry Requirements" },
  { id: "cost-of-living", label: "Cost of Living" },
  { id: "work-visa", label: "Post-Study Work Visa" },
  { id: "jobs", label: "Jobs After Graduation" },
  { id: "student-visa", label: "Student Visa" },
  { id: "faq", label: "FAQs" },
];

const CountryPage = () => {
  const dispatch = useAppDispatch();
  const searchParams = useParams();
  const { countryList } = useAppSelector((state) => state.country);
  const { id } = searchParams;

  const matchedCountry = countryList.find(
    (item) => formatCountryUrl(item.country_name) === id,
  );

  const countryId = matchedCountry?.country_id;

  const [loading, setLoading] = useState(true);
  const [universityList, setUniversityList] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const isScrollingRef = useRef(false);

  const [country, setCountry] = useState<CountryInfo>({
    id: 0,
    bgImg: "",
    countryName: "",
    currency: "",
    description: "",
    popularFields: [],
    pathway: "",
    mobilePathway: "",
    chartData: {
      lowestFee: 0,
      highestFee: 0,
    },
    earnWhileStudy: [],
    slides: [],
  });

  const availableTabs = React.useMemo(() => {
    return ALL_COUNTRY_TABS.filter((tab) => {
      switch (tab.id) {
        case "overview":
          return (
            (country?.highlights && country.highlights.length > 0) ||
            Boolean(country?.description)
          );
        case "why-study":
          return Boolean(country?.whyStudyReasons?.reasons?.length);
        case "universities":
          return Boolean(universityList?.length);
        case "cost-of-studying":
          return Boolean(country?.costOfStudying?.tuitionTable?.length);
        case "entry-requirements":
          return Boolean(country?.entryRequirements?.requirementsTable?.length);
        case "cost-of-living":
          return (
            Boolean(country?.costOfLiving?.livingTable?.length) ||
            Boolean(country?.costOfLiving?.cityCosts?.length) ||
            Boolean(country?.costOfLiving?.ruleOfThumb?.description)
          );
        case "work-visa":
          return (
            Boolean(country?.postStudyWorkVisa?.topCards?.length) ||
            Boolean(country?.postStudyWorkVisa?.timelineSteps?.length)
          );
        case "jobs":
          return (
            Boolean(country?.jobsAfterGraduation?.jobCards?.length) ||
            Boolean(country?.jobsAfterGraduation?.partTimeCallout?.description)
          );
        case "student-visa":
          return Boolean(country?.studentVisaProcess?.steps?.length);
        case "faq":
          return Boolean(country?.faqsSection?.items?.length);
        default:
          return true;
      }
    });
  }, [country, universityList]);

  useEffect(() => {
    setLoading(true);
    async function universities() {
      try {
        const apiUrl = `/country/universities/${countryId}?fields=college_id,college_name,address_info,college_slug,country_id,founded_year,rank_details,category,currency,popularity,specialisation_category,application_fees,offer_tat&courseLevel=All`;
        const response = await axiosInstance.get(apiUrl);
        setUniversityList(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error to fetch universities list", error);
        setLoading(false);
      }
    }
    universities();
  }, [dispatch, countryId]);

  const getCountryById = async (id: number, countryName?: string) => {
    return await client.fetch(getCountryByIdQuery, { id, countryName: countryName || "" });
  };

  useEffect(() => {
    const fetchCountry = async () => {
      if (!countryId && !matchedCountry?.country_name) return;

      try {
        const data = await getCountryById(Number(countryId), matchedCountry?.country_name);
        if (data) {
          setCountry({
            ...data,
            slides:
              data.slides?.map((slide: any) => ({
                ...slide,
                countryName: data.countryName,
              })) || [],
          });
        }
      } catch (error) {
        console.error("Error fetching country:", error);
      }
    };

    fetchCountry();
  }, [countryId]);

  // Scroll spy active tab detection
  useEffect(() => {
    if (loading) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const scrollPosition = window.scrollY + 140;

      for (const tab of availableTabs) {
        const el = document.getElementById(tab.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveTab(tab.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, availableTabs]);

  const handleTabClick = (tabId: string) => {
    const el = document.getElementById(tabId);
    if (el) {
      isScrollingRef.current = true;
      setActiveTab(tabId);
      const top = el.getBoundingClientRect().top + window.scrollY - 75;
      window.scrollTo({
        top,
        behavior: "smooth",
      });

      setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  };

  return (
    <>
      {loading ? (
        <ApiLoader gif="/assets/gif/passport.gif" />
      ) : (
        <div className="university_page_gradient pt-[60px] md:pt-[90px] min-h-screen">
          <NewNavBar />

          {/* Hero Banner */}
          <Banner country={country} />

          {/* Sticky Navigation Bar */}
          {availableTabs.length > 0 && (
            <CountryStickyTabs
              tabs={availableTabs}
              activeTab={activeTab}
              onTabClick={handleTabClick}
            />
          )}

          <div className="relative container 2xl:max-w-[1628px] mx-auto px-4 sm:px-6">
            {/* OVERVIEW SECTION */}
            <div id="overview" className="scroll-mt-24">
              <CountryHighlights country={country} />
            </div>

            {/* WHY STUDY SECTION */}
            <div id="why-study" className="scroll-mt-24">
              <CountryWhyStudy country={country} />
            </div>

            {/* TOP UNIVERSITIES SECTION (UNTOUCHED / UNCHANGED) */}
            {universityList && universityList?.length > 0 && (
              <div id="universities" className="scroll-mt-24">
                <TopUniversity
                  country={country}
                  universityList={universityList}
                />
              </div>
            )}

            {/* <TryAiCourseFindBanner
              className="!rounded-2xl max-w-[1580px] mx-auto my-10"
              radios={false}
            /> */}

            {/* COST OF STUDYING SECTION */}
            <div id="cost-of-studying" className="scroll-mt-24">
              <CountryCostOfStudying country={country} />
            </div>

            {/* ENTRY REQUIREMENTS SECTION */}
            <div id="entry-requirements" className="scroll-mt-24">
              <CountryEntryRequirements country={country} />
            </div>

            {/* COST OF LIVING SECTION */}
            <div id="cost-of-living" className="scroll-mt-24">
              <CountryCostOfLiving country={country} />
            </div>

            {/* POST STUDY WORK VISA SECTION */}
            <div id="work-visa" className="scroll-mt-24">
              <CountryPostStudyWorkVisa country={country} />
            </div>

            {/* JOBS AFTER GRADUATION SECTION */}
            <div id="jobs" className="scroll-mt-24">
              <CountryJobsAfterGraduation country={country} />
            </div>

            {/* STUDENT VISA PROCESS SECTION */}
            <div id="student-visa" className="scroll-mt-24">
              <CountryStudentVisaProcess country={country} />
            </div>

            {/* FAQS SECTION */}
            <div id="faq" className="scroll-mt-24">
              <CountryFaqs country={country} />
            </div>

            {/* BOTTOM CALLOUT BANNER CTA */}
            <CountryBottomCta country={country} />
          </div>

          <Footer />
        </div>
      )}
    </>
  );
};

export default CountryPage;
